import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import {
  Student,
  StudentLessonLessonRow,
  StudentLessonStatsResponse,
  fetchStudentLessonStats,
} from "../../api/students";
import { UI_LOADING } from "../../constants/uiLabels";
import "./StudentLessonStatsDrawer.css";

type Props = {
  student: Student;
  onClose: () => void;
};

const STATUS_LABELS: Record<string, string> = {
  planned: "Planlı",
  in_progress: "Devam",
  completed: "Tamamlandı",
  cancelled: "İptal",
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

function attendanceLabel(row: StudentLessonLessonRow): { text: string; tone: string } {
  if (row.status === "cancelled") return { text: "İptal", tone: "muted" };
  if (!row.has_report) {
    if (new Date(row.ends_at).getTime() < Date.now()) {
      return { text: "Rapor bekliyor", tone: "warn" };
    }
    return { text: "—", tone: "muted" };
  }
  if (row.attended === true) return { text: "Katıldı", tone: "ok" };
  if (row.attended === false) return { text: "Gelmedi", tone: "bad" };
  return { text: "—", tone: "muted" };
}

export function StudentLessonStatsDrawer({ student, onClose }: Props) {
  const [data, setData] = useState<StudentLessonStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchStudentLessonStats(student.id, {}, { signal: controller.signal })
      .then(setData)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Ders özeti yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [student.id]);

  const stats = data?.stats;

  return (
    <div className="kp-student-stats-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="kp-student-stats-drawer kp-card"
        role="dialog"
        aria-labelledby="kp-student-stats-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="kp-student-stats-drawer__head">
          <div>
            <h2 id="kp-student-stats-title">{student.full_name}</h2>
            <p className="kp-student-stats-drawer__sub">Ders özeti · tüm dönem (iptal hariç yazılan)</p>
          </div>
          <button type="button" className="kp-btn kp-btn--ghost" onClick={onClose} aria-label="Kapat">
            ✕
          </button>
        </header>

        {loading && <div className="kp-loading">{UI_LOADING}</div>}
        {error && <div className="kp-error">{error}</div>}

        {!loading && !error && stats && (
          <>
            <div className="kp-student-stats-kpis">
              <div className="kp-student-stats-kpi">
                <span className="kp-student-stats-kpi__num">{stats.reserved_lesson_count}</span>
                <span className="kp-student-stats-kpi__label">Rezervasyon</span>
              </div>
              <div className="kp-student-stats-kpi kp-student-stats-kpi--ok">
                <span className="kp-student-stats-kpi__num">{stats.attended}</span>
                <span className="kp-student-stats-kpi__label">Katıldı</span>
              </div>
              <div className="kp-student-stats-kpi kp-student-stats-kpi--bad">
                <span className="kp-student-stats-kpi__num">{stats.no_show}</span>
                <span className="kp-student-stats-kpi__label">Gelmedi</span>
              </div>
            </div>
            <p className="kp-student-stats-drawer__sub kp-student-stats-drawer__sub--inline">
              İlerleme: katıldı / rezervasyon
              {stats.attendance_rate !== null ? ` (%${stats.attendance_rate})` : ""}
              {stats.report_pending > 0 ? ` · ${stats.report_pending} rapor bekliyor` : ""}
            </p>

            {stats.reserved_lesson_count > 0 && (
              <div
                className="kp-student-stats-bar"
                role="img"
                aria-label={`Rezervasyon ${stats.reserved_lesson_count}, katıldı ${stats.attended}, gelmedi ${stats.no_show}`}
              >
                <span
                  className="kp-student-stats-bar__ok"
                  style={{
                    width: `${(stats.attended / stats.reserved_lesson_count) * 100}%`,
                  }}
                />
                <span
                  className="kp-student-stats-bar__bad"
                  style={{
                    width: `${(stats.no_show / stats.reserved_lesson_count) * 100}%`,
                  }}
                />
              </div>
            )}

            <p className="kp-student-stats-extra">
              {stats.report_completion_rate !== null && (
                <span>Rapor tamamlama %{stats.report_completion_rate}</span>
              )}
              {stats.planned_upcoming > 0 && (
                <span>
                  {stats.report_completion_rate !== null ? " · " : ""}
                  {stats.planned_upcoming} gelecek rezervasyon
                </span>
              )}
              {stats.cancelled > 0 && (
                <span>
                  {(stats.report_completion_rate !== null || stats.planned_upcoming > 0) ? " · " : ""}
                  {stats.cancelled} iptal (sayılmaz)
                </span>
              )}
            </p>

            <div className="kp-student-stats-lessons">
              <h3>Son dersler</h3>
              {data.lessons.length === 0 ? (
                <p className="kp-empty">Kayıtlı ders yok.</p>
              ) : (
                <table className="kp-student-stats-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Eğitmen</th>
                      <th>Durum</th>
                      <th>Katılım</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lessons.map((row) => {
                      const att = attendanceLabel(row);
                      return (
                        <tr key={row.appointment_id}>
                          <td>{fmtDateTime(row.starts_at)}</td>
                          <td>{row.instructor_name}</td>
                          <td>{STATUS_LABELS[row.status] || row.status}</td>
                          <td>
                            <span className={`kp-student-stats-att kp-student-stats-att--${att.tone}`}>
                              {att.text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
